import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query'
import { APIEndpoints } from '../constants/api-endpoints'
import { QueryKeys } from './_query_keys'
import { HttpRequest } from '../services/_http'
import moment from 'moment'
import { useNotificationBar } from 'context/notificationContext'
import jsFileDownlaod from 'js-file-download'
import * as XLSX from 'xlsx'

export const useVideoTrackingReport = (
  searchOptions?: any,
  options?: UseQueryOptions
): UseQueryResult<any> => {
  const method = 'GET'
  const queryKey = [QueryKeys.VIDEO_TRACKING_REPORT, searchOptions]

  return useQuery({
    queryKey,
    queryFn: () => HttpRequest({
      url: `${APIEndpoints.VideoTrackingReport}`,
      params: searchOptions,
      method
    }),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    ...options
  })
}

export const useVideoTrackingAppVersions = (
  options?: UseQueryOptions
): UseQueryResult<any> => {
  const method = 'GET'
  const queryKey = [QueryKeys.VIDEO_TRACKING_APP_VERSIONS]

  return useQuery({
    queryKey,
    queryFn: () => HttpRequest({
      url: `${APIEndpoints.VideoTrackingAppVersions}`,
      method
    }),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    ...options
  })
}

export const useExportVideoTrackingReport = () => {
  const method = 'GET'
  const notification = useNotificationBar()
  return (additionalQueryParams: Record<string, unknown> = {}) => {
    let processingIdx = notification.push({
      type: 'info',
      title: 'Processing file please wait',
      message: 'Your data will be downloaded soon ...'
    })
    HttpRequest({
      url: `${APIEndpoints.VideoTrackingExport}`,
      method,
      params: {
        ...additionalQueryParams
      },
      responseType: 'blob'
    }).then(data => {
      jsFileDownlaod(data?.data, `video-tracking_${moment().format("DD-MMM-YYYY-hh:mm:ss")}.xlsx`)
      processingIdx && notification.hide(processingIdx)
      notification.push({
        type: 'success',
        title: 'Downloaded',
        message: 'Exported data has been downloaded successfully !'
      })
    }).catch(error => {
      processingIdx && notification.hide(processingIdx)
      notification.push({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export data. Please try again.'
      })
    })
  }
}

export const useExportVisibleVideoTrackingRows = () => {
  const notification = useNotificationBar()

  const formatDateTime = (value: any) => {
    if (!value) return '-'
    return moment(value).format('DD MMM YYYY, hh:mm:ss.SSS A')
  }

  const formatMobile = (value: any) => {
    if (!value) return '-'
    return `'${value}`
  }

  const formatSourceLocation = (value: string) => {
    const SOURCE_LOCATION_LABELS: Record<string, string> = {
      showpage: 'Show Page',
      homepage: 'Home Page',
    }

    if (!value) return '-'

    if (SOURCE_LOCATION_LABELS[value]) return SOURCE_LOCATION_LABELS[value]

    return value
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
  }

  return (rows: Record<string, any>[] = []) => {
    if (!rows.length) {
      notification.push({
        type: 'error',
        title: 'Export Failed',
        message: 'No visible data available to export.',
      })
      return
    }

    try {
      // Transform data into required format
      const formattedData = rows.map((item) => ({
        Contact: formatMobile(item.mobile),
        Episode: item.episode_name || '-',
        'Pre Loaded':item.preloaded || '-',
        'App Version': item.appVersion || '-',
        'Platform': item.deviceType || '-',
        'Decoder Init Time (ms)': item.decoderInitMs || '-',
        'Decoder TS (ms)': item.decoderTsMs ?? '-',
        'Playing Started Time (ms)': item.playingStartedTimeMs ?? '-',
        'Device Model': item.device_model || '-',
        'Device OS': item.device_os || '-',
        Resolution: item.resolution || '-',
        Network: item.network || '-',
        RAM: item.ram || '-',
        Bandwidth: item.bandwidth || '-',
          // ✅ NEW VIDEO METRICS
        'Preload Resolution Max Width': item.preloadResolutionMaxWidth ?? '-',
        'Preload Resolution Max Height': item.preloadResolutionmaxHeight ?? '-',
        'Player Resolution Max Width': item.PlayerResolutionMaxWidth ?? '-',
        'Player Resolution Max Height': item.PlayerResolutionmaxHeight ?? '-',
        "Bandwidth player": item.bandwidthPlayer ?? '-',
        'Min Buffer (ms)': item.minBufferMs ?? '-',
        'Max Buffer (ms)': item.maxBufferMs ?? '-',
        'Buffer For Playback (ms)': item.bufferForPlaybackMs ?? '-',
        'Buffer After Rebuffer (ms)': item.bufferAfterRebufferMs ?? '-',

        'Total Bytes Loaded': item.totalBytesLoaded ?? '-',
        'TotalLoadTimeMs': item.totalLoadTimeMs ??  '-',

        'Source Location': formatSourceLocation(item.sourceLocation),
        'Thumbnail Click Time': formatDateTime(item.thumbnailClickTime_readable),
        'First Frame Time': formatDateTime(item.firstFrameTime_readable),
        'Total Load Time (ms)': item.totalLoadTime_ms ?? '-',
        Date: formatDateTime(item.date_time),
      }))

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(formattedData)

      // Auto column width (nice UX)
      const columnWidths = Object.keys(formattedData[0]).map(() => ({ wch: 25 }))
      worksheet['!cols'] = columnWidths

      // Create workbook
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Video Tracking')

      // Download file
      XLSX.writeFile(
        workbook,
        `video-tracking_current-page_${moment().format('DD-MMM-YYYY-hh:mm:ss')}.xlsx`
      )

      notification.push({
        type: 'success',
        title: 'Downloaded',
        message: 'Visible page data has been downloaded successfully.',
      })
    } catch (error) {
      notification.push({
        type: 'error',
        title: 'Export Failed',
        message: 'Something went wrong while exporting data.',
      })
    }
  }
}